import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Clock, AlertCircle, CheckCircle2, Ban, Hourglass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarLayout } from "@/layouts/SidebarLayout";
import { EmailSidebar } from "@/components/email/EmailSidebar";
import { CampaignControls } from '@/components/email/campaigns/CampaignControls';
import { HermesCampaignContacts } from '@/components/email/campaigns/HermesCampaignContacts';
import { RecipientEmailPreview } from '@/components/email/campaigns/RecipientEmailPreview';
import { useHermesCampaign, useHermesCampaignJobs, useUpdateHermesCampaign } from '@/hooks/useHermesCampaigns';
import { useHermesEmailAccount } from '@/hooks/useHermesEmailAccounts';
import { computeCampaignJobCounts } from '@/lib/email/job-status';
import { resolveCampaignTotals, describeCampaignWindow } from '@/lib/email/campaign-totals';
import type { HermesEmailCampaign } from '@/types/hermes-campaign';

export function CampaignDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const { data: campaign, isLoading } = useHermesCampaign(id);
  const updateCampaign = useUpdateHermesCampaign();
  const [emailActiveTab, setEmailActiveTab] = useState("campaigns");

  useEffect(() => {
    if (campaign) {
      setCampaignName(campaign.name);
    }
  }, [campaign?.name]);

  const handleNameSave = async () => {
    if (!id || !campaign || campaignName.trim() === campaign.name) {
      setIsEditing(false);
      return;
    }
    try {
      await updateCampaign.mutateAsync({
        campaignId: id,
        updates: { name: campaignName.trim() },
      });
    } catch {
      // toast is handled by the mutation hook
    }
    setIsEditing(false);
  };

  // Prevent navigation away from sequence tab when editing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (activeTab === 'sequence') {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeTab]);

  const backButton = (
    <Button
      variant="ghost"
      onClick={() => navigate('/inbox')}
      className="mb-4"
    >
      <ArrowLeft className="h-4 w-4 mr-2" />
      Back to Campaigns
    </Button>
  );

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        {backButton}
        <div className="flex items-center justify-center h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="container mx-auto py-8">
        {backButton}
        <div className="text-center py-8 text-muted-foreground">
          Campaign not found
        </div>
      </div>
    );
  }

  return (
    <SidebarLayout
      sidebar={
        <EmailSidebar
          activePage={emailActiveTab}
          onPageChange={setEmailActiveTab}
        />
      }
    >
      <div className="h-full flex flex-col">
        <div className="p-8 space-y-6">
        {backButton}

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            {isEditing ? (
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                className="text-2xl font-semibold h-auto py-1 max-w-md"
                autoFocus
              />
            ) : (
              <h1
                className="text-2xl font-semibold cursor-pointer hover:bg-accent/50 px-2 py-1 rounded"
                onClick={() => setIsEditing(true)}
              >
                {campaign.name}
              </h1>
            )}
          </div>

          <div className="flex items-center gap-4">
            <CampaignControls campaignId={campaign.id} variant="compact" />
          </div>
        </div>

        <div className="space-y-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="recipients">Recipients</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="pt-4">
              <HermesCampaignOverview campaign={campaign} />
            </TabsContent>

            <TabsContent value="recipients" className="pt-4">
              <HermesCampaignContacts campaignId={campaign.id} campaign={campaign} />
            </TabsContent>

            <TabsContent value="preview" className="pt-4">
              <RecipientEmailPreview campaignId={campaign.id} campaign={campaign} />
            </TabsContent>
          </Tabs>
        </div>
        </div>
      </div>
    </SidebarLayout>
  );
}

function HermesCampaignOverview({ campaign }: { campaign: HermesEmailCampaign }) {
  // Pull per-job rows so we can split the campaign's `emails_failed` counter
  // into "real failures" vs. "skipped (recipient suppressed)". The campaign
  // record itself doesn't carry that split — see PR #504 (circuit-breaker
  // fix); the per-job error_message is the only signal.
  const { data: jobs = [], isSuccess: jobsLoaded } = useHermesCampaignJobs(campaign.id);
  const jobCounts = useMemo(() => computeCampaignJobCounts(jobs), [jobs]);

  // Totals come from the durable campaign row, never from surviving queue
  // rows: hermes_email_queue_jobs is purged 7 days after sending (#859), so
  // a job-derived fallback would silently decay toward zero as rows age out.
  //
  // The window is withheld until the query has actually succeeded. An in-flight query also
  // yields `jobs = []`, and treating that as an empty window would flash "records have been
  // removed" on every healthy campaign for a frame. `isSuccess` rather than `!isLoading`:
  // react-query v5 reports isLoading=false for a disabled query that has never fetched.
  const totals = resolveCampaignTotals(campaign, jobsLoaded ? jobCounts : null);
  const { recipients: total, sent, pending, skipped, failed, completionPercentage } = totals;
  const windowNotice = describeCampaignWindow(totals);

  // A campaign moves no faster than its sender mailbox's daily cap: the
  // account's daily_email_limit bounds real throughput, and jobs past their
  // scheduled day roll forward FIFO into that cap. Forecast = pending ÷ cap.
  const { data: account } = useHermesEmailAccount(campaign.email_account_id);
  const dailyLimit = account?.daily_email_limit ?? 0;
  const daysLeft = pending > 0 && dailyLimit > 0 ? Math.ceil(pending / dailyLimit) : null;

  return (
    <div className="space-y-6">
      {skipped > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 px-3 py-2 text-sm">
          <Ban className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
          <span className="text-amber-800 dark:text-amber-200">
            {skipped} recipient{skipped !== 1 ? 's were' : ' was'} skipped because they previously unsubscribed, bounced, or marked email as spam. Contact them through another channel if needed.
          </span>
        </div>
      )}
      <div className={`grid grid-cols-2 gap-4 ${skipped > 0 ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Recipients</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <Mail className="h-5 w-5 text-muted-foreground" />
              {total}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Sent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              {sent}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              {pending}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Time to complete</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <Hourglass className="h-5 w-5 text-amber-500" />
              {daysLeft !== null ? `~${daysLeft} day${daysLeft === 1 ? '' : 's'}` : pending === 0 ? 'Done' : '—'}
            </div>
            {account && (
              <div className="text-[10px] text-muted-foreground mt-1">
                drains at up to {dailyLimit}/day via {account.email_address}
              </div>
            )}
          </CardContent>
        </Card>
        {skipped > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Skipped</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                <Ban className="h-5 w-5 text-amber-600" />
                {skipped}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">unsubscribed / bounced</div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              {failed}
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Caveat the breakdown against the total: hermes_email_queue_jobs rows are purged 7
          days after sending (#859), so the surviving window can undercount the split above
          or drop to 0 entirely. describeCampaignWindow owns the wording and, crucially,
          returns null when the window isn't known yet. */}
      {windowNotice && <div className="text-sm text-muted-foreground">{windowNotice}</div>}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Campaign Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Subject</span>
            <span className="font-medium">{campaign.subject_template}</span>
          </div>
          {campaign.scheduled_start && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Scheduled Start</span>
              <span>{new Date(campaign.scheduled_start).toLocaleString()}</span>
            </div>
          )}
          {campaign.scheduled_end && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estimated Completion</span>
              <span>{new Date(campaign.scheduled_end).toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Created</span>
            <span>{campaign.created_at ? new Date(campaign.created_at).toLocaleString() : '—'}</span>
          </div>
          {total > 0 && (
            <div className="pt-2">
              {/* Progress reflects terminal states (sent + skipped + failed)
                  — skipped recipients aren't "in progress", their journey is
                  done (they just won't receive the email). Widths normalise
                  against the terminal count when the durable counters overshoot
                  recipient_count (2 legacy campaigns do), so the segments never
                  overflow the bar; the headline % is the resolver's clamped one. */}
              {(() => {
                const terminal = sent + skipped + failed;
                const span = Math.max(total, terminal);
                const width = (n: number) => (span > 0 ? (n / span) * 100 : 0);
                return (
                  <>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Progress</span>
                      <span>{Math.round(completionPercentage)}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 flex overflow-hidden">
                      <div
                        className="bg-primary h-2 transition-all"
                        style={{ width: `${width(sent)}%` }}
                        title={`${sent} sent`}
                      />
                      <div
                        className="bg-amber-500 h-2 transition-all"
                        style={{ width: `${width(skipped)}%` }}
                        title={`${skipped} skipped`}
                      />
                      <div
                        className="bg-destructive h-2 transition-all"
                        style={{ width: `${width(failed)}%` }}
                        title={`${failed} failed`}
                      />
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
